import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logError } from '../services/errorLog';
import { logger } from '../utils/logger';
import { appConfig } from '../config';
import { applyUpdateNow, getUpdateInputs, isNewerVersion, subscribeUpdateStatus } from '../utils/updateStatus';
import { SELF_HELP_COPY } from './SelfHelpSheet';
import { dismissSelfHelp } from '../utils/selfHelp';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// sessionStorage key to prevent infinite reload loops on persistent chunk errors
const CHUNK_RELOAD_KEY = 'rrp_chunk_reload_attempted';

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload CSS')
  );
}

export class ErrorBoundary extends Component<Props, State> {
  /** While the crash screen shows: redraw when the live version arrives (it's often fetched after the crash). */
  private unsubscribeVersion: (() => void) | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Auto-reload on stale deployment chunk errors (new deploy invalidates old hashes).
    // Guard with sessionStorage to avoid infinite loops if the error persists after reload.
    if (isChunkLoadError(error) && !sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
      window.location.reload();
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) return;
    logger.error('ErrorBoundary caught an error:', error, errorInfo);
    logError(error, `ReactErrorBoundary${errorInfo.componentStack ? ` — ${errorInfo.componentStack.trim().split('\n')[0]}` : ''}`, 'react_boundary');
    // This screen shows the update message itself; don't pop it again after Try Again.
    if (this.isOldCopyScreen(error)) dismissSelfHelp();
    else if (!this.props.fallback && !this.unsubscribeVersion) {
      this.unsubscribeVersion = subscribeUpdateStatus(() => {
        if (!this.state.hasError) return;
        if (this.isOldCopyScreen(this.state.error)) dismissSelfHelp();
        this.forceUpdate();
      });
    }
  }

  componentWillUnmount() {
    this.stopWatchingVersion();
  }

  stopWatchingVersion() {
    this.unsubscribeVersion?.();
    this.unsubscribeVersion = null;
  }

  isOldCopyScreen(error: Error | null): boolean {
    return !this.props.fallback && !!error && !isChunkLoadError(error)
      && isNewerVersion(getUpdateInputs().liveVersion, appConfig.version);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.stopWatchingVersion();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Tier 1: an old copy that crashed gets told why and offered the update. Update now
      // only — there's no working screen behind this for a "remind me" to return to.
      if (this.isOldCopyScreen(this.state.error)) {
        const copy = SELF_HELP_COPY.update;
        return (
          <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center border border-slate-200 dark:border-slate-700 space-y-4">
              <h1 className="text-xl font-bold text-slate-800 dark:text-white">{copy.heading}</h1>
              <p className="text-slate-600 dark:text-slate-400">{copy.reason}</p>
              <p className="text-sm rounded-lg px-3 py-2 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">✓ {copy.safe}</p>
              <button
                onClick={applyUpdateNow}
                className="tap-row w-full min-h-[48px] bg-brand-go text-brand-navy dark:text-brand-navy rounded-xl font-bold hover:bg-brand-go-deep transition flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {copy.action}
              </button>
              <button onClick={this.handleReset} className="tap-row text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg px-4 py-2 transition">
                Try Again
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center border border-slate-200 dark:border-slate-700">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            
            <h1 className="text-xl font-bold text-slate-800 dark:text-white mb-2">
              The app hit a problem
            </h1>
            
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {this.state.error && isChunkLoadError(this.state.error)
                ? 'There is a new version of the app. Tap Reload Page to get it.'
                : 'Tap Try Again. If it keeps happening, tap Reload Page. If it still happens, tell the office.'}
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="bg-slate-100 dark:bg-slate-900 rounded-lg p-3 mb-6 text-left overflow-auto max-h-32">
                <code className="text-xs text-red-600 dark:text-red-400 font-mono">
                  {this.state.error.message}
                </code>
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="tap-row px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="tap-row px-4 py-2 bg-brand-go text-brand-navy dark:text-brand-navy rounded-lg hover:bg-brand-go-deep transition flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

